import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';

import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ContentService } from '../../services/content.service';
import { SdmUtilService } from '../../services/sdm-util.service';

type OrderDir = 'asc' | 'desc';
interface OrderBy { column: string; order: OrderDir; }
interface AlarmFilter {
	Locations: string[];
	Severity: number[];
	OrderBy: OrderBy;
	alarmGrouping?: boolean;
}

@Component({
	selector: 'alarm-component',
	standalone: true,
	templateUrl: 'components/alarm/alarm.component.html',
	imports: [
		...SHARED_IMPORTS,
		MatSlideToggleModule,
		MatIconModule
	]
})
export class AlarmComponent implements OnInit {
	private content = inject(ContentService);
	private http = inject(HttpClient);
	private router = inject(Router);
	private sdmUtil = inject(SdmUtilService);

	public severities: Record<number, string> = { 0: 'Non-critical', 10: 'Critical' };
	public orderColumns: Record<string, string> = {};
	loading = false;

	public filter: AlarmFilter = {
		Locations: [],
		Severity: [],
		OrderBy: { column: 'StartedTS', order: 'desc' },
		alarmGrouping: false
	};

	public openPanel: 'filter' | 'orderby' | null = null;
	private allErrors: any[] = [];
	private viewErrors: any[] = [];

	public locations: any[] = [];
	public ready = false;
	public withNavbar = true;
	public getContent: (key: string, ...args: any[]) => string;



	constructor() {
		this.orderColumns = {
			StartedTS: this.content.getContent('Alarm.SortByTime'),
			LocationString: this.content.getContent('Alarm.SortByLocation'),
		};
		this.getContent = this.content.getContent.bind(this.content);
	}

	toId(val: any): string { return String(val); }

	togglePanel(panel: 'filter' | 'orderby'): void {
		this.openPanel = (this.openPanel === panel ? null : panel);
	}

	ngOnInit(): void {
		const persisted = this.safeGetLocalStorage<Partial<AlarmFilter>>('alarm_filter');
		if (persisted) {
			this.filter = {
				Locations: persisted.Locations ?? [],
				Severity: persisted.Severity ?? [],
				OrderBy: persisted.OrderBy ?? { column: 'StartedTS', order: 'desc' },
				alarmGrouping: persisted.alarmGrouping ?? false
			};
		}
		this.loadLocations().then(() => this.loadAllAlarms());
	}

	private async loadLocations(): Promise<void> {
		const res: any = await this.http.get('/api/Location/GetAllLocationAreas').toPromise();
		this.locations = Array.isArray(res) ? res : (res?.data ?? []);

		// TODO: Get location parameter from Angular Router if needed
		const seededLocationName = undefined; // this.route.snapshot.params?.['Location'] as string | undefined;
		if (seededLocationName) {
			const loc = this.locations.find(l => l.LocationString === seededLocationName);
			this.filter = {
				...this.filter,
				Locations: loc ? [String(loc.Id)] : [],
				OrderBy: { column: 'StartedTS', order: 'desc' }
			};
			this.persistFilter();
		}
	}

	private loadAllAlarms(): void {
		this.loading = true;
		this.http.get<any[]>('/api/Alarm/AlarmOverview').subscribe({
			next: list => {
				this.allErrors = list ?? [];
				this.applyLocalFilterSort();
				this.ready = true;
			},
			error: err => {
				console.error('Failed to load alarms', err);
			},
			complete: () => {
				this.loading = false;
			}
		});
	}

	private applyLocalFilterSort(): void {
		const selectedIds = new Set(this.filter.Locations ?? []);
		const idToName = new Map(this.locations.map(l => [String(l.Id), l.LocationString]));
		const selectedNames = new Set([...selectedIds].map(id => idToName.get(id)).filter(Boolean) as string[]);
		const hasLocFilter = selectedNames.size > 0;

		const hasSeverityFilter = (this.filter.Severity?.length ?? 0) > 0;
		const selectedSev = new Set(this.filter.Severity ?? []);

		// Filter
		let arr = this.allErrors.filter(e => {
			const okLoc = !hasLocFilter ? true : selectedNames.has(String(e?.LocationString));
			const okSev = !hasSeverityFilter ? true : selectedSev.has(Number(e?.Severity));
			return okLoc && okSev;
		});

		// Sort
		const col = this.filter?.OrderBy?.column ?? 'StartedTS';
		const dir = this.filter?.OrderBy?.order === 'asc' ? 1 : -1;
		arr = [...arr].sort((a, b) => {
			const va = (a?.[col] ?? '');
			const vb = (b?.[col] ?? '');
			if (va == null && vb == null) return 0;
			if (va == null) return 1;
			if (vb == null) return -1;
			if (va < vb) return -1 * dir;
			if (va > vb) return 1 * dir;
			return 0;
		});

		this.viewErrors = arr;
	}

	getSelectedLocations(): string[] {
		if (!this.locations?.length || !this.filter?.Locations?.length) return [];
		const map = new Map(this.locations.map(l => [String(l.Id), l.LocationString]));
		return this.filter.Locations.map(id => map.get(String(id)) || null).filter(Boolean) as string[];
	}

	getSelectedSeverities(): string[] {
		return (this.filter.Severity ?? []).map(v => this.severities[v]);
	}

	getSeverity(label: string): string {
		return label === 'Non-critical'
			? this.content.getContent('AlarmOverview.NonCritical')
			: this.content.getContent('AlarmOverview.Critical');
	}

	getErrorPastTime(error: any): string {
		return this.sdmUtil.getErrorPastTime(error, this.content);
	}

	OrderBy(column: string): void {
		if (this.filter?.OrderBy?.column === column) {
			this.filter.OrderBy = {
				column,
				order: this.filter.OrderBy.order === 'asc' ? 'desc' : 'asc'
			};
		} else {
			this.filter.OrderBy = { column, order: 'asc' };
		}
		this.persistFilter();
		this.applyLocalFilterSort();
	}

	applyFilter(): void {
		this.persistFilter();
		this.applyLocalFilterSort();
		this.openPanel = null;
	}

	closeFilterPanel(): void {
		// Close filter panel without applying changes (for Escape key)
		this.openPanel = null;
	}

	clearFilters(): void {
		this.filter = {
			...this.filter,
			Locations: [],
			Severity: []
		};

		this.persistFilter();
		this.applyLocalFilterSort();
		this.openPanel = null;
	}

	changeGrouping(): void {
		this.persistFilter();
	}

	toggleLocation(id: string): void {
		const set = new Set(this.filter.Locations ?? []);
		set.has(id) ? set.delete(id) : set.add(id);
		this.filter.Locations = Array.from(set);
	}

	toggleSeverity(value: number): void {
		const set = new Set(this.filter.Severity ?? []);
		set.has(value) ? set.delete(value) : set.add(value);
		this.filter.Severity = Array.from(set);
	}

	unitClick(unitId: any): void {
		this.router.navigate(['/UnitDetail', unitId]);
	}

	goBack(): void {
		// TODO: Implement proper navigation history tracking if needed
		// For now, default to Main page
		this.router.navigate(['/Main']);
	}

	getCriticalAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS == null && e?.Severity === 10);
	}
	getNonCriticalAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS == null && e?.Severity === 0);
	}
	getCriticalAndNonCriticalAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS == null && (e?.Severity === 0 || e?.Severity === 10));
	}
	geDismissableAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS != null);
	}
	geDismissableCriticalAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS != null && e?.Severity === 10);
	}
	geDismissableNonCriticalAlarms(): any[] {
		return this.viewErrors.filter(e => e?.EndedTS != null && e?.Severity === 0);
	}

	trackByErrorId = (_: number, e: any) => e?.Id ?? e;

	trackByKey = (_: number, kv: { key: string }) => kv.key;

	private persistFilter(): void {
		localStorage.setItem('alarm_filter', JSON.stringify(this.filter));
	}

	private safeGetLocalStorage<T>(key: string): T | null {
		try {
			const raw = localStorage.getItem(key);
			return raw ? (JSON.parse(raw) as T) : null;
		} catch {
			return null;
		}
	}
}
